import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface TeamsAuthenticationConfiguration {
  ClientIDSecretId: string;
  ClientSecretSecretId: string | null;
  ConnectionName: string;
  Scopes: string[] | null;
  Authority: string | null;
}

export interface TeamsIntegration {
  IntegrationId: string;
  MSTenantID: string;
  BotId: string;
  ServiceUrl: string;
  AuthenticationConfiguration: TeamsAuthenticationConfiguration;
}

export interface TeamsIntegrationDto {
  MSTenantID: string;
  BotId: string;
  ServiceUrl: string;
  AuthenticationConfiguration: TeamsAuthenticationConfiguration;
}

@Injectable({
  providedIn: 'root'
})
export class TeamsIntegrationService {
  private api: AxiosInstance | null = null;
  private readonly platformId = inject(PLATFORM_ID);
  private readonly authService = inject(KeycloakAuthService);
  private readonly apiConfig = inject(ApiConfigService);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.api = axios.create({
        headers: {
          'Content-Type': 'application/json'
        }
      });

      this.api.interceptors.request.use((config) => {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/${tenantId}`;

        const token = this.authService.accessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      });
    }
  }

  async getTeamsIntegrations(): Promise<TeamsIntegration[] | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get('/teams');
      return response.data;
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }

  async getTeamsIntegration(integrationId: string): Promise<TeamsIntegration | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get(`/${integrationId}/teams`);
      return response.data;
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }

  async createTeamsIntegration(integrationId: string, dto: TeamsIntegrationDto): Promise<string | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.post(`/${integrationId}/teams`, dto);
      return response.data;
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }

  async updateTeamsIntegration(integrationId: string, dto: TeamsIntegrationDto): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.put(`/${integrationId}/teams`, dto);
      return response;
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }

  async deleteTeamsIntegration(integrationId: string): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.delete(`/${integrationId}/teams`);
      return response;
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }
}
